import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DigestUnsubscribe — public page hit from the "Pause these digest emails"
 * link inside the fortnightly Health Digest. Uses the HMAC-signed token so
 * the patient does not need to log in.
 */
export default function DigestUnsubscribe() {
  const [params] = useSearchParams();
  const [state, setState] = useState("pending"); // pending | success | error
  const [reason, setReason] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("error"); setReason("missing_token"); return;
    }
    axios
      .get(`${API_BASE}/digest/unsubscribe`, { params: { token } })
      .then((r) => {
        if (r.data.opted_out) setState("success");
        else { setState("error"); setReason(r.data.reason || "unknown"); }
      })
      .catch(() => { setState("error"); setReason("network"); });
  }, [params]);

  return (
    <div
      data-testid="digest-unsub-page"
      className="min-h-screen flex items-center justify-center bg-[color:var(--mano-bg)] p-6"
    >
      <div className="max-w-md w-full rounded-3xl bg-white border border-[color:var(--mano-border)] p-8 text-center space-y-4">
        {state === "pending" && (
          <>
            <div className="uppercase-label">Working on it…</div>
            <p className="text-[color:var(--mano-ink-soft)] text-sm">
              Pausing your fortnightly digest.
            </p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
            <h1 className="font-display text-2xl">Digest paused</h1>
            <p className="text-[color:var(--mano-ink-soft)] text-sm">
              We&rsquo;ve stopped the fortnightly digest emails. Nothing else in your
              Manodayam account changes — your dashboard, alerts, and care are all
              still active.
            </p>
            <Link
              to="/me"
              data-testid="digest-unsub-go-my-page"
              className="inline-flex items-center gap-2 mt-2 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold text-sm"
            >
              Open My Page <ArrowRight size={14} />
            </Link>
            <p className="text-[10px] text-[color:var(--mano-ink-soft)] mt-4">
              Changed your mind? You can re-enable the digest anytime from
              My Page → Health Indices.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={44} className="mx-auto text-red-600" />
            <h1 className="font-display text-2xl">Link didn&rsquo;t work</h1>
            <p className="text-[color:var(--mano-ink-soft)] text-sm">
              {reason === "invalid_token"
                ? "This unsubscribe link is invalid or has expired. Please sign in and pause the digest from My Page instead."
                : "Something went wrong. Please try again or pause the digest from My Page after signing in."}
            </p>
            <Link
              to="/me"
              data-testid="digest-unsub-fallback"
              className="inline-flex items-center gap-2 mt-2 px-5 py-2.5 rounded-full border border-[color:var(--mano-border)] font-semibold text-sm"
            >
              Sign in and open My Page <ArrowRight size={14} />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
